import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'sonner';
import {
    Landmark,
    RefreshCw,
    Upload,
    CheckCircle,
    AlertCircle,
    FilePlus,
    FileText,
    Undo2,
    X as XIcon,
    TrendingUp,
    TrendingDown,
} from 'lucide-react';

import axios from '../../../services/AxiosInstance';
import { getApiUrl } from '../../../utils/ApiUtils';
import PageHeader from '../../layouts/PageHeader';
import { ListToolbar, FilterChips } from '../Common/ListToolbar';
import { EmptyState } from '../Common/EmptyState';
import { useListControls } from '../../../hooks/useListControls';
import SkatteverketImportModal from './SkatteverketImportModal';
import CreateVerificationFromTransactionModal from '../Banking/CreateVerificationFromTransactionModal';
import { brandSelectTheme } from '../Common/reactSelectTheme';
import { getVerificationById, sortRowsByAccount } from '../../../services/MatchingService';
import { Eyebrow, KonteringSlip, formatCurrency, formatDate } from '../Banking/BankhandelserPage';

// The Skattekonto page (2026-07-27 rebuild) — same design language and
// vocabulary as Bankhändelser, adapted to the tax rail's simpler reality:
// a skattekonto row either NEEDS a booking (rules from the skattekonto
// pack prefill it) or its bokföring already EXISTS (kopplad/klar). CSV
// import is the one way in; a future Skatteverket API connection slots
// in behind the same entry point.

const FETCH_LIMIT = 500;
const PAGE_SIZE = 50;
const config = { withCredentials: true };

const FILTER_TODO = 'todo';
const FILTER_MATCHED = 'matched';
const FILTER_ALL = 'all';
const FILTER_DONE = 'done';
// DISJOINT piles, mirroring Bankhändelser: Att bokföra = needs a booking,
// Att koppla = bokföring exists (suggested — the user confirms), Klara =
// done. The engine never links inferred matches on this rail either.
const FILTERS = [
    { key: FILTER_TODO, label: 'Att bokföra' },
    { key: FILTER_MATCHED, label: 'Att koppla' },
    { key: FILTER_ALL, label: 'Alla' },
    { key: FILTER_DONE, label: 'Klara' },
];

const SORT_OPTIONS = [
    { value: 'date_desc', label: 'Senaste först' },
    { value: 'date_asc', label: 'Äldsta först' },
    { value: 'amount_desc', label: 'Största belopp' },
];

const DATE_RANGE_OPTIONS = [
    { value: 'all', label: 'Alla datum' },
    { value: 'm3', label: 'Senaste 3 mån' },
    { value: 'year', label: 'I år' },
    { value: 'custom', label: 'Anpassat…' },
];

const isDone = (line) => line.reconciliation_status !== 'unmatched';
const hasSuggestedLink = (line) => line.reconciliation_status === 'unmatched' && line.suggested_verification_id;

function rowLabel(line) {
    if (hasSuggestedLink(line)) {
        const nr = `${line.suggested_verification_series || ''}${line.suggested_verification_number || ''}`;
        return {
            text: nr ? `Koppla till verifikat ${nr}` : 'Koppla till verifikat',
            badge: 'Förslag finns',
            badgeColor: 'var(--primary)',
        };
    }
    if (line.reconciliation_status === 'unmatched') {
        return { text: 'Behöver bokföras', badge: 'Att bokföra', badgeColor: '#b45309' };
    }
    if (line.matched_verification_id) {
        const nr = `${line.verification_series || ''}${line.verification_number || ''}`;
        return {
            text: nr ? `Kopplad till verifikat ${nr}` : 'Kopplad till verifikat',
            badge: 'Klar',
            badgeColor: '#10b981',
        };
    }
    return { text: 'Avstämd — inget verifikat behövdes', badge: 'Klar', badgeColor: '#10b981' };
}

function StatementRow({ line, isSelected, isPending, onSelect }) {
    const amount = parseFloat(line.amount);
    const isInflow = amount > 0;
    const label = rowLabel(line);
    return (
        <button
            type="button"
            onClick={onSelect}
            className="matching-list-item"
            style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                border: 'none',
                borderBottom: '1px solid #f3f4f6',
                borderLeft: isSelected ? '3px solid var(--primary)' : '3px solid transparent',
                background: isSelected ? 'var(--rgba-primary-1)' : 'transparent',
                padding: '10px 16px',
                cursor: 'pointer',
                opacity: isPending ? 0.5 : 1,
                transition: 'opacity 0.15s ease',
            }}
            aria-busy={isPending || undefined}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {isInflow
                    ? <TrendingUp size={14} style={{ color: 'var(--bs-success)', flexShrink: 0 }} />
                    : <TrendingDown size={14} style={{ color: '#dc2626', flexShrink: 0 }} />}
                <span style={{ fontWeight: 600, fontSize: '0.8125rem', color: '#111827' }}>
                    {formatDate(line.transaction_date)}
                </span>
                <span style={{ fontSize: '0.8125rem', color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                    {line.description || '(ingen beskrivning)'}
                </span>
                <span style={{ fontWeight: 600, fontSize: '0.8125rem', color: isInflow ? '#10b981' : '#dc2626', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                    {formatCurrency(amount)}
                </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2, paddingLeft: 22 }}>
                <span style={{ fontSize: '0.75rem', color: '#6b7280', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {label.text}
                    {isPending && <Spinner animation="border" size="sm" className="ms-2" style={{ width: 12, height: 12 }} />}
                </span>
                <span style={{ flexShrink: 0, fontSize: 11, fontWeight: 600, color: label.badgeColor, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <CheckCircle size={12} />
                    {label.badge}
                </span>
            </div>
        </button>
    );
}

export default function SkattekontoPage() {
    const { companyId } = useParams();

    const [statements, setStatements] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [error, setError] = useState(null);
    // FILTER_TODO is the default, so the clean /skattekonto URL still opens on
    // "Att bokföra" without carrying ?filter=todo around.
    const {
        search: searchText,
        setSearch: setSearchText,
        debouncedSearch,
        filter: activeFilter,
        setFilter: setActiveFilter,
        sort: sortKey,
        setSort: setSortKey,
    } = useListControls({ defaultFilter: FILTER_TODO, defaultSort: 'date_desc' });
    const [dateRange, setDateRange] = useState('all');
    const [customFrom, setCustomFrom] = useState('');
    const [customTo, setCustomTo] = useState('');
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

    const [selectedId, setSelectedId] = useState(null);
    const [pendingId, setPendingId] = useState(null);
    const [paneSuggestion, setPaneSuggestion] = useState(null);
    const [linkedVerification, setLinkedVerification] = useState(null);
    const [actionBusy, setActionBusy] = useState(false);
    const fetchToken = useRef(0);

    const [showImportModal, setShowImportModal] = useState(false);
    const [showBookingModal, setShowBookingModal] = useState(false);
    const [bookingPrefillRows, setBookingPrefillRows] = useState(null);

    const load = useCallback(async () => {
        if (!companyId) return;
        try {
            const url = getApiUrl(`${companyId}/accounting/reconciliation/skatteverket/statements`);
            const { data } = await axios.get(url, { ...config, params: { limit: FETCH_LIMIT } });
            setStatements(data?.statements || []);
            setError(null);
        } catch (err) {
            setError(err?.response?.data?.message || err.message || 'Kunde inte hämta skattekontotransaktioner');
        } finally {
            setIsLoading(false);
            setIsRefreshing(false);
        }
    }, [companyId]);

    useEffect(() => { setIsLoading(true); load(); }, [load]);

    // ── Pane data: rule suggestion for unbooked rows, linked verifikat
    //    for matched rows. Same deferred-open pattern as Bankhändelser:
    //    the clicked row dims until data is ready. ──
    useEffect(() => {
        if (!pendingId) return;
        const token = ++fetchToken.current;
        setPaneSuggestion(null);
        setLinkedVerification(null);

        const line = statements.find((l) => l.statement_id === pendingId);
        const done = () => {
            if (fetchToken.current !== token) return;
            setPendingId(null);
            setSelectedId(pendingId);
        };

        if (line && hasSuggestedLink(line)) {
            getVerificationById(companyId, line.suggested_verification_id)
                .then((data) => {
                    if (fetchToken.current !== token) return;
                    setLinkedVerification(data?.verification || data || null);
                })
                .catch(() => {})
                .finally(done);
        } else if (line && line.reconciliation_status === 'unmatched') {
            const url = getApiUrl(`${companyId}/accounting/reconciliation/skatteverket/statements/${pendingId}/verification-suggestions`);
            axios.get(url, config)
                .then(({ data }) => {
                    if (fetchToken.current !== token) return;
                    const suggestions = data?.suggestions || [];
                    setPaneSuggestion(suggestions.find((s) => s.strategy === 'rule') || suggestions[0] || null);
                })
                .catch(() => {})
                .finally(done);
        } else if (line && line.matched_verification_id) {
            getVerificationById(companyId, line.matched_verification_id)
                .then((data) => {
                    if (fetchToken.current !== token) return;
                    setLinkedVerification(data?.verification || data || null);
                })
                .catch(() => {})
                .finally(done);
        } else {
            done();
        }
    }, [companyId, pendingId, statements]);

    const handleSelectRow = useCallback((line) => {
        if (pendingId) return;
        if (selectedId === line.statement_id) {
            setSelectedId(null);
            return;
        }
        setPendingId(line.statement_id);
        if (selectedId) setSelectedId(line.statement_id);
    }, [selectedId, pendingId]);

    const handleMutated = useCallback(() => {
        setSelectedId(null);
        setShowBookingModal(false);
        setBookingPrefillRows(null);
        setIsRefreshing(true);
        load();
    }, [load]);

    // ── Filtering pipeline (date → pills → search → sort → window) ──
    const dateBounds = useMemo(() => {
        const iso = (d) => d.toISOString().slice(0, 10);
        const today = new Date();
        switch (dateRange) {
            case 'm3': { const d = new Date(today); d.setMonth(d.getMonth() - 3); return { from: iso(d), to: null }; }
            case 'year': return { from: `${today.getFullYear()}-01-01`, to: null };
            case 'custom': return { from: customFrom || null, to: customTo || null };
            default: return { from: null, to: null };
        }
    }, [dateRange, customFrom, customTo]);

    const dateFiltered = useMemo(() => statements.filter((l) => {
        if (!dateBounds.from && !dateBounds.to) return true;
        const d = String(l.transaction_date || '').slice(0, 10);
        if (dateBounds.from && d < dateBounds.from) return false;
        if (dateBounds.to && d > dateBounds.to) return false;
        return true;
    }), [statements, dateBounds]);

    const filterGroups = useMemo(() => {
        const groups = { [FILTER_TODO]: [], [FILTER_MATCHED]: [], [FILTER_ALL]: dateFiltered, [FILTER_DONE]: [] };
        for (const line of dateFiltered) {
            if (hasSuggestedLink(line)) groups[FILTER_MATCHED].push(line);
            else if (isDone(line)) groups[FILTER_DONE].push(line);
            else groups[FILTER_TODO].push(line);
        }
        return groups;
    }, [dateFiltered]);

    const visibleLines = useMemo(() => {
        const lines = filterGroups[activeFilter] || [];
        const needle = debouncedSearch.toLowerCase();
        const filtered = !needle ? lines : lines.filter((l) =>
            (l.description || '').toLowerCase().includes(needle)
            || String(l.amount || '').includes(needle));
        const sorted = [...filtered];
        const dateOf = (l) => String(l.transaction_date || '');
        switch (sortKey) {
            case 'date_asc': sorted.sort((a, b) => dateOf(a).localeCompare(dateOf(b))); break;
            case 'amount_desc': sorted.sort((a, b) => Math.abs(parseFloat(b.amount)) - Math.abs(parseFloat(a.amount))); break;
            default: sorted.sort((a, b) => dateOf(b).localeCompare(dateOf(a)));
        }
        return sorted;
    }, [filterGroups, activeFilter, debouncedSearch, sortKey]);

    useEffect(() => { setVisibleCount(PAGE_SIZE); }, [activeFilter, debouncedSearch, sortKey, dateRange, customFrom, customTo]);
    const windowedLines = useMemo(() => visibleLines.slice(0, visibleCount), [visibleLines, visibleCount]);

    const selectedLine = useMemo(
        () => visibleLines.find((l) => l.statement_id === selectedId) || null,
        [visibleLines, selectedId],
    );

    // Current skattekonto saldo = the newest balance-carrying row (full
    // set — the date filter never changes a point-in-time fact).
    const currentSaldo = useMemo(() => {
        let latest = null;
        for (const line of statements) {
            if (line.balance == null || line.balance === '') continue;
            const d = String(line.transaction_date || '');
            if (!latest || d > latest.date) latest = { date: d, balance: parseFloat(line.balance) };
        }
        return latest;
    }, [statements]);

    const todoCount = (filterGroups[FILTER_TODO] || []).length;
    const doneCount = (filterGroups[FILTER_DONE] || []).length;

    // ── Actions ──
    // Koppla (single): the user's confirm of a suggested link. Link-only,
    // per-row Ångra afterwards — no dialog needed.
    const handleKoppla = useCallback(async (line) => {
        const target = line || selectedLine;
        if (!target) return;
        setActionBusy(true);
        try {
            const url = getApiUrl(`${companyId}/accounting/reconciliation/skatteverket/statements/${target.statement_id}/confirm-link`);
            await axios.post(url, {}, config);
            toast.success('Raden kopplades till verifikatet.');
            handleMutated();
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Kunde inte koppla raden');
        } finally {
            setActionBusy(false);
        }
    }, [companyId, selectedLine, handleMutated]);

    // Koppla alla — same visible per-row cascade as Bankhändelser.
    const [linkAllBusy, setLinkAllBusy] = useState(false);
    const [linkProgress, setLinkProgress] = useState(null);
    const [processingId, setProcessingId] = useState(null);
    const [leavingIds, setLeavingIds] = useState(() => new Set());
    const handleKopplaAlla = useCallback(async () => {
        const lines = [...(filterGroups[FILTER_MATCHED] || [])];
        if (lines.length === 0) return;
        setLinkAllBusy(true);
        setLinkProgress({ done: 0, total: lines.length });
        let linked = 0;
        let skipped = 0;
        try {
            for (const line of lines) {
                setProcessingId(line.statement_id);
                try {
                    const url = getApiUrl(`${companyId}/accounting/reconciliation/skatteverket/statements/${line.statement_id}/confirm-link`);
                    await axios.post(url, {}, config);
                    linked += 1;
                    setLeavingIds((prev) => new Set(prev).add(line.statement_id));
                    setTimeout(() => {
                        setStatements((prev) => prev.map((l) =>
                            l.statement_id === line.statement_id
                                ? { ...l, reconciliation_status: 'verified', matched_verification_id: l.suggested_verification_id, verification_series: l.suggested_verification_series, verification_number: l.suggested_verification_number, suggested_verification_id: null }
                                : l));
                        setLeavingIds((prev) => {
                            const next = new Set(prev);
                            next.delete(line.statement_id);
                            return next;
                        });
                    }, 300);
                } catch (rowError) {
                    skipped += 1;
                }
                setProcessingId(null);
                setLinkProgress((p) => ({ done: (p?.done || 0) + 1, total: p?.total || lines.length }));
            }
            if (linked > 0) toast.success(`${linked} ${linked === 1 ? 'rad' : 'rader'} kopplade — inget nytt bokfördes.`);
            if (skipped > 0) toast.info(`${skipped} rader kunde inte kopplas och ligger kvar.`, { duration: 8000 });
            load();
        } finally {
            setLinkAllBusy(false);
            setLinkProgress(null);
            setProcessingId(null);
        }
    }, [companyId, filterGroups, load]);

    const handleUnlink = useCallback(async () => {
        if (!selectedLine) return;
        setActionBusy(true);
        try {
            const url = getApiUrl(`${companyId}/accounting/reconciliation/skatteverket/statements/${selectedLine.statement_id}/match`);
            await axios.delete(url, config);
            toast.success('Kopplingen togs bort.');
            handleMutated();
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Kunde inte ta bort kopplingen');
        } finally {
            setActionBusy(false);
        }
    }, [companyId, selectedLine, handleMutated]);

    const handleImportSuccess = useCallback(() => {
        setShowImportModal(false);
        toast.success('Skattekontotransaktionerna importerades — regler och matchning körs i bakgrunden.', { duration: 8000 });
        // The server-side post-import chain (auto-apply → matching → bank
        // re-run) works asynchronously; refresh shortly after.
        setTimeout(() => load(), 4000);
        handleMutated();
    }, [handleMutated, load]);

    const openBooking = useCallback((prefillRows) => {
        setBookingPrefillRows(prefillRows && prefillRows.length > 0 ? sortRowsByAccount(prefillRows) : null);
        setShowBookingModal(true);
    }, []);

    return (
        <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <PageHeader
                    icon={Landmark}
                    title="Skattekonto"
                    subtitle="Importera, bokför och stäm av ditt skattekonto hos Skatteverket."
                    actions={
                        <>
                        <Button variant="outline-secondary" size="sm" onClick={() => { setIsRefreshing(true); load(); }} disabled={isRefreshing}>
                            <RefreshCw size={14} className={isRefreshing ? 'me-1 spin' : 'me-1'} />
                            Uppdatera
                        </Button>
                        <Button variant="primary" size="sm" onClick={() => setShowImportModal(true)}>
                            <Upload size={14} className="me-1" />
                            Importera
                        </Button>
                        </>
                    }
                />

                {/* Summary strip */}
                {!isLoading && statements.length > 0 && (
                    <div className="matching-state-box" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 16, padding: '12px 16px' }}>
                        <span style={{ fontSize: 13, color: '#374151', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <CheckCircle size={15} style={{ color: '#10b981' }} />
                            <strong>{doneCount}</strong> klara
                        </span>
                        <span style={{ fontSize: 13, color: '#374151', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <FilePlus size={15} style={{ color: '#b45309' }} />
                            <strong>{todoCount}</strong> att bokföra
                        </span>
                    </div>
                )}

                {/* The 1630 connection + saldo — the page's anchor fact */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        border: '1px solid #e5e7eb', background: '#fff',
                        borderRadius: 999, padding: '4px 12px',
                        fontSize: 12, color: '#374151', fontVariantNumeric: 'tabular-nums',
                    }}>
                        <Landmark size={12} style={{ color: '#6b7280' }} />
                        Skattekonto
                        <span style={{ color: '#9ca3af' }}>bokförs på</span>
                        <strong>1630</strong>
                        <span style={{ color: '#6b7280' }}>Avräkning för skatter och avgifter</span>
                        {currentSaldo && (
                            <>
                                <span style={{ color: '#9ca3af' }}>·</span>
                                <span style={{ color: '#9ca3af' }}>Saldo</span>
                                <strong>{formatCurrency(currentSaldo.balance)}</strong>
                            </>
                        )}
                    </span>
                </div>

                <ListToolbar
                    search={{
                        value: searchText,
                        onChange: setSearchText,
                        placeholder: 'Sök beskrivning eller belopp…',
                        ariaLabel: 'Sök bland skattekontotransaktioner',
                    }}
                    sort={{ options: SORT_OPTIONS, value: sortKey, onChange: (v) => setSortKey(v || 'date_desc'), ariaLabel: 'Sortera' }}
                >
                    <div style={{ minWidth: 170 }}>
                        <Select
                            options={DATE_RANGE_OPTIONS}
                            value={DATE_RANGE_OPTIONS.find((o) => o.value === dateRange) || DATE_RANGE_OPTIONS[0]}
                            onChange={(opt) => setDateRange(opt?.value || 'all')}
                            isSearchable={false}
                            theme={brandSelectTheme}
                            styles={{ control: (b) => ({ ...b, minHeight: 38, fontSize: 13 }), option: (b) => ({ ...b, fontSize: 13 }) }}
                            aria-label="Filtrera på datum"
                        />
                    </div>
                    {dateRange === 'custom' && (
                        <>
                            <Form.Control type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} aria-label="Från datum" style={{ width: 150 }} />
                            <span style={{ color: '#9ca3af', fontSize: 13 }}>–</span>
                            <Form.Control type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} aria-label="Till datum" style={{ width: 150 }} />
                        </>
                    )}
                </ListToolbar>

                <FilterChips
                    chips={FILTERS.map((c) => ({ ...c, count: (filterGroups[c.key] || []).length }))}
                    value={activeFilter}
                    onChange={(key) => { setActiveFilter(key); setSelectedId(null); }}
                />

                {activeFilter === FILTER_MATCHED && (filterGroups[FILTER_MATCHED] || []).length > 0 && (
                    <div
                        className="matching-state-box"
                        style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '10px 16px' }}
                    >
                        <span style={{ fontSize: 13, color: '#374151' }}>
                            Dessa rader har redan en bokföring — koppla ihop dem så är de klara.
                            Inget nytt bokförs och varje rad kan ångras.
                        </span>
                        <Button
                            variant="success"
                            size="sm"
                            style={{ marginLeft: 'auto' }}
                            onClick={handleKopplaAlla}
                            disabled={linkAllBusy}
                        >
                            {linkAllBusy ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Kopplar… {linkProgress ? `${linkProgress.done}/${linkProgress.total}` : ''}
                                </>
                            ) : (
                                <>
                                    <CheckCircle size={14} className="me-1" />
                                    Koppla alla {(filterGroups[FILTER_MATCHED] || []).length}
                                </>
                            )}
                        </Button>
                    </div>
                )}

                {error && (
                    <Alert variant="danger" onClose={() => setError(null)} dismissible className="mb-0">
                        <AlertCircle size={14} className="me-2" />
                        {error}
                    </Alert>
                )}

                {/* List + inspector, same split animation as Bankhändelser */}
                <div className={`bank-split${selectedLine ? ' open' : ''}`}>
                    <div>
                        {isLoading ? (
                            <div className="matching-state-box" style={{ padding: 40, textAlign: 'center' }}>
                                <Spinner animation="border" size="sm" />
                                <span className="ms-2">Laddar transaktioner…</span>
                            </div>
                        ) : visibleLines.length === 0 ? (
                            statements.length === 0 ? (
                                <div className="matching-state-box" style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
                                    <Landmark size={32} style={{ opacity: 0.4 }} />
                                    <p style={{ marginTop: 12, marginBottom: 4, fontWeight: 500 }}>
                                        Importera ditt skattekonto
                                    </p>
                                    <p style={{ fontSize: 13, marginBottom: 12 }}>
                                        Ladda ner kontoutdraget från Skatteverket (CSV) och importera
                                        det här — reglerna bokför de vanliga händelserna åt dig.
                                    </p>
                                    <Button variant="primary" size="sm" onClick={() => setShowImportModal(true)}>
                                        <Upload size={14} className="me-1" />
                                        Importera transaktioner
                                    </Button>
                                </div>
                            ) : activeFilter === FILTER_TODO && !searchText ? (
                                <div className="matching-state-box" style={{ padding: 40, textAlign: 'center' }}>
                                    <CheckCircle size={32} style={{ color: '#10b981' }} />
                                    <p style={{ marginTop: 12, marginBottom: 4, fontWeight: 600, color: '#111827' }}>
                                        Allt är bokfört
                                    </p>
                                    <p style={{ fontSize: 13, marginBottom: 0, color: '#6b7280' }}>
                                        {doneCount > 0 ? `${doneCount} rader är klara.` : 'Importera fler transaktioner när du vill.'}
                                    </p>
                                </div>
                            ) : (
                                <EmptyState
                                    variant="no-matches"
                                    query={debouncedSearch}
                                    title={debouncedSearch ? 'Inga träffar' : 'Inga transaktioner i denna vy'}
                                    description="Byt filter för att se andra transaktioner."
                                />
                            )
                        ) : (
                            <div className="matching-list-box">
                                {windowedLines.map((line) => (
                                    <div
                                        key={line.statement_id}
                                        style={{
                                            transition: 'opacity 300ms ease, transform 300ms ease',
                                            opacity: leavingIds.has(line.statement_id) ? 0 : 1,
                                            transform: leavingIds.has(line.statement_id) ? 'translateX(16px)' : 'none',
                                        }}
                                    >
                                        <StatementRow
                                            line={line}
                                            isSelected={line.statement_id === selectedId}
                                            isPending={line.statement_id === pendingId || line.statement_id === processingId}
                                            onSelect={() => handleSelectRow(line)}
                                        />
                                    </div>
                                ))}
                                {visibleLines.length > windowedLines.length && (
                                    <div style={{ padding: 12, textAlign: 'center', borderTop: '1px solid #f3f4f6' }}>
                                        <Button variant="outline-secondary" size="sm" onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}>
                                            Visa fler (visar {windowedLines.length} av {visibleLines.length})
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {selectedLine && (
                        <div
                            className="bank-inspector"
                            style={{
                                position: 'sticky', top: 16, background: '#fff',
                                border: '1px solid #e5e7eb', borderRadius: 12,
                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                                display: 'flex', flexDirection: 'column',
                                maxHeight: 'calc(100vh - 110px)', overflow: 'hidden',
                            }}
                        >
                            <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid #f3f4f6', flexShrink: 0 }}>
                                <div className="d-flex align-items-start justify-content-between" style={{ marginBottom: 4 }}>
                                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                                        <span style={{ fontSize: '1.25rem', fontWeight: 700, color: parseFloat(selectedLine.amount) > 0 ? '#10b981' : '#dc2626', fontVariantNumeric: 'tabular-nums' }}>
                                            {formatCurrency(parseFloat(selectedLine.amount))}
                                        </span>
                                        <span style={{ fontSize: 11, fontWeight: 600, color: rowLabel(selectedLine).badgeColor }}>
                                            {rowLabel(selectedLine).badge}
                                        </span>
                                    </div>
                                    <Button variant="link" size="sm" onClick={() => setSelectedId(null)} style={{ color: '#6b7280', padding: 4 }} aria-label="Stäng">
                                        <XIcon size={18} />
                                    </Button>
                                </div>
                                <div style={{ fontSize: '0.8125rem', color: '#374151' }}>
                                    {formatDate(selectedLine.transaction_date)} · {selectedLine.description || '(ingen beskrivning)'}
                                </div>
                                <div style={{ fontSize: '0.6875rem', color: '#9ca3af', marginTop: 2 }}>
                                    {[
                                        selectedLine.period_reference ? `Period ${selectedLine.period_reference}` : null,
                                        selectedLine.balance != null && selectedLine.balance !== '' ? `Saldo ${formatCurrency(parseFloat(selectedLine.balance))}` : null,
                                    ].filter(Boolean).join(' · ')}
                                </div>
                            </div>

                            <div key={`${selectedLine.statement_id}`} className="bank-pane-in" style={{ padding: '18px 20px 20px', overflowY: 'auto' }}>
                                {hasSuggestedLink(selectedLine) ? (
                                    <>
                                        <Eyebrow color="#b45309">Redan bokförd?</Eyebrow>
                                        <p style={{ fontSize: '0.875rem', color: '#1f2937', marginBottom: 6 }}>
                                            Raden ser ut att motsvara verifikatet nedan, som redan finns i din bokföring.
                                        </p>
                                        {linkedVerification && (
                                            <div style={{ marginBottom: 12 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8125rem', color: '#374151' }}>
                                                    <FileText size={14} style={{ color: 'var(--primary)', flexShrink: 0 }} />
                                                    <strong>{linkedVerification.series}{linkedVerification.number}</strong>
                                                    <span style={{ color: '#9ca3af' }}>·</span>
                                                    <span>{formatDate(linkedVerification.date)}</span>
                                                </div>
                                                <KonteringSlip rows={(linkedVerification.transactions || []).map((t) => ({
                                                    account_number: t.account_number,
                                                    account_name: t.account_name,
                                                    debit: t.transaction_type === 'debit' ? t.amount : 0,
                                                    credit: t.transaction_type === 'credit' ? t.amount : 0,
                                                }))} />
                                            </div>
                                        )}
                                        <div className="d-flex flex-wrap gap-2">
                                            <Button variant="primary" size="sm" disabled={actionBusy} onClick={() => handleKoppla(selectedLine)}>
                                                <CheckCircle size={14} className="me-1" />
                                                Koppla till verifikatet
                                            </Button>
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="text-muted p-1"
                                                disabled={actionBusy}
                                                onClick={() => openBooking(null)}
                                            >
                                                Bokför manuellt i stället
                                            </Button>
                                        </div>
                                        <div style={{ fontSize: '0.6875rem', color: '#9ca3af', marginTop: 6 }}>
                                            Inget nytt bokförs — raden kopplas ihop med det befintliga verifikatet.
                                        </div>
                                    </>
                                ) : selectedLine.reconciliation_status === 'unmatched' ? (
                                    <>
                                        <Eyebrow>Förslag</Eyebrow>
                                        {paneSuggestion && paneSuggestion.strategy === 'rule' ? (
                                            <>
                                                <p style={{ fontSize: '0.875rem', color: '#1f2937', marginBottom: 6 }}>
                                                    Regeln <strong>{paneSuggestion.ruleName}</strong> föreslår den här bokföringen.
                                                </p>
                                                <KonteringSlip rows={paneSuggestion.rows || []} />
                                            </>
                                        ) : (
                                            <p style={{ fontSize: '0.875rem', color: '#1f2937', marginBottom: 6 }}>
                                                Ingen regel matchar raden — bokför den manuellt nedan.
                                            </p>
                                        )}
                                        <div className="d-flex flex-wrap gap-2 mt-2">
                                            <Button
                                                variant="primary"
                                                size="sm"
                                                disabled={actionBusy}
                                                onClick={() => openBooking(paneSuggestion?.strategy === 'rule' ? paneSuggestion.rows : null)}
                                            >
                                                <FilePlus size={14} className="me-1" />
                                                Bokför
                                            </Button>
                                        </div>
                                        {paneSuggestion?.strategy === 'rule' && (
                                            <div style={{ fontSize: '0.6875rem', color: '#9ca3af', marginTop: 6 }}>
                                                Konteringen är förifylld från regeln — granska och spara i nästa steg.
                                            </div>
                                        )}
                                    </>
                                ) : selectedLine.matched_verification_id ? (
                                    <>
                                        <Eyebrow color="#10b981">Redan bokförd</Eyebrow>
                                        <p style={{ fontSize: '0.875rem', color: '#1f2937', marginBottom: 6 }}>
                                            Raden är kopplad till verifikatet nedan.
                                        </p>
                                        {linkedVerification && (
                                            <div style={{ marginBottom: 12 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8125rem', color: '#374151' }}>
                                                    <FileText size={14} style={{ color: 'var(--primary)', flexShrink: 0 }} />
                                                    <strong>{linkedVerification.series}{linkedVerification.number}</strong>
                                                    <span style={{ color: '#9ca3af' }}>·</span>
                                                    <span>{formatDate(linkedVerification.date)}</span>
                                                </div>
                                                <KonteringSlip rows={(linkedVerification.transactions || []).map((t) => ({
                                                    account_number: t.account_number,
                                                    account_name: t.account_name,
                                                    debit: t.transaction_type === 'debit' ? t.amount : 0,
                                                    credit: t.transaction_type === 'credit' ? t.amount : 0,
                                                }))} />
                                            </div>
                                        )}
                                        {selectedLine.reconciliation_status === 'auto_matched' && (
                                            <Button variant="link" size="sm" className="text-muted p-0" disabled={actionBusy} onClick={handleUnlink}>
                                                {actionBusy ? <Spinner animation="border" size="sm" className="me-1" /> : <Undo2 size={14} className="me-1" />}
                                                Ångra koppling
                                            </Button>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <Eyebrow color="#10b981">Klar</Eyebrow>
                                        <p style={{ fontSize: '0.875rem', color: '#1f2937', marginBottom: 0 }}>
                                            Raden är avstämd — inget verifikat behövdes.
                                        </p>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <SkatteverketImportModal
                show={showImportModal}
                onHide={() => setShowImportModal(false)}
                onImportSuccess={handleImportSuccess}
            />

            <CreateVerificationFromTransactionModal
                show={showBookingModal}
                onHide={() => { setShowBookingModal(false); setBookingPrefillRows(null); }}
                transaction={selectedLine}
                initialRows={bookingPrefillRows}
                onSuccess={() => { toast.success('Verifikatet skapades.'); handleMutated(); }}
            />
        </>
    );
}
